/**
 * The machinery every append-only log the client syncs shares: the TMS event streams and the acknowledgement logs.
 *
 * A log is read in delta pages: rows with `createdAt` after a `since`, oldest first. Rows are insert-only, so a walk
 * from `since` that reaches the head leaves the client holding every row created in `(since, clock − lag]`. That is
 * the invariant the coverage model below is built on: the client persists the spans of the `createdAt` axis it holds,
 * and every load — cold boot, warm boot, gap fill, poll — is the one operation of filling the gaps between the floor
 * and now, newest first. What differs per log (its fetch, its apply, how deep it is held) stays with the log.
 */

import { createLogger } from "@/logger";
import type { SyncContext } from "@/models/sync/syncTargets";

const log = createLogger("appsync");

/**
 * How much of a log a cold boot loads first: the recent rows Dispatch shows, landed before any older history.
 *
 * Shared by every log the client syncs. The rest of each log's window loads in the background behind it.
 */
export const INITIAL_COLD_BOOT_WINDOW = { hours: 6 } as const;

/**
 * The rung edges a cold boot's window is split at, in hours before now: the newest hour lands first, then the rest
 * of the recent window, then everything older in the background.
 */
export const COLD_BOOT_LADDER_HOURS: readonly number[] = [1, INITIAL_COLD_BOOT_WINDOW.hours];

/**
 * How far behind the server's clock a walk that reached the head claims coverage.
 *
 * `createdAt` is the writer's statement time and the read runs on a replica, so a row can carry a `createdAt` earlier
 * than a read that did not see it: it committed, or replicated, after the read. Claiming only up to the clock less
 * this lag re-reads such rows on the next walk; repeats are keyed on `id`. Wider than the server's own hold-back.
 */
export const COVERAGE_HEAD_LAG_MS = 30_000;

const HOUR_MS = 60 * 60_000;

/**
 * How far above the floor the oldest span may begin before the sliver below it counts as a gap.
 *
 * The floor is `now − depth` on the server's clock as the client estimates it, and that estimate is recalibrated from
 * every response's one-second `Date` header, so it can step back a little between two reads. Without this slack, a
 * step back of a few milliseconds opens a gap of a few milliseconds under the oldest span, and closing it costs a whole
 * page: the walk stops only once a page reaches past the gap's top. A minute is far above the clock's jitter and far
 * below anything a reader of the window would miss.
 */
export const COVERAGE_FLOOR_SLACK_MS = 60_000;

/** One span of the `createdAt` axis the store holds completely: every row with `createdAt` in `(from, to]`. */
export type CoveredSpan = { from: string; to: string };

/** What the client persists per log: its held spans, ascending, disjoint, and merged. */
export type LogCoverage = { spans: CoveredSpan[] };

/** Read/write side of the persisted coverage; production passes through to the bootstrap registry. */
export type CoverageStore = {
  /** Whatever was stored, unvalidated: `normalizeCoverage` is the one place that reads it into a `LogCoverage`. */
  get: (key: string, workspaceId: string) => Promise<unknown>;
  set: (key: string, workspaceId: string, coverage: LogCoverage) => Promise<void>;
  /** Drops every key of the workspace that starts with `prefix` and is not in `keep`; a store without it keeps all. */
  prune?: (prefix: string, workspaceId: string, keep: ReadonlySet<string>) => Promise<void>;
};

/** A gap to fill: rows created in `(lo, hi]`; `hi` null means up to the head of the log. */
export type Gap = { lo: Date; hi: Date | null };

const EMPTY: LogCoverage = { spans: [] };

/**
 * The stored coverage made safe to plan from.
 *
 * A span that does not parse, or runs backwards, is dropped; one reaching past `now` is clamped to it (a clock that
 * ran ahead); one below `floor` is clamped up to it or dropped when nothing of it remains; overlapping and touching
 * spans are merged. Anything unreadable reads as no coverage, so the log cold boots rather than trusting it.
 */
export function normalizeCoverage(coverage: unknown, floor: Date, now: Date): LogCoverage {
  const spans = (coverage as { spans?: unknown } | undefined)?.spans;
  if (!Array.isArray(spans)) return EMPTY;
  let normalized: LogCoverage = EMPTY;
  for (const span of spans as { from?: unknown; to?: unknown }[]) {
    const from = Math.max(parse(span?.from), floor.getTime());
    const to = Math.min(parse(span?.to), now.getTime());
    if (Number.isNaN(from) || Number.isNaN(to) || from > to) continue;
    normalized = mergeSpan(normalized, { from: iso(from), to: iso(to) });
  }
  return normalized;
}

/**
 * `coverage` with `span` folded in: inserted in order and merged with every span it overlaps or touches.
 *
 * A span whose `to` is behind its `from` is taken as the point `from`, so a walk's first page can never record a
 * backwards span.
 */
export function mergeSpan(coverage: LogCoverage, span: CoveredSpan): LogCoverage {
  const from = parse(span.from);
  const to = Math.max(from, parse(span.to));
  const spans: CoveredSpan[] = [];
  let merged = { from, to };
  let placed = false;
  for (const held of coverage.spans) {
    const current = { from: parse(held.from), to: parse(held.to) };
    if (current.to < merged.from) {
      // Wholly below the new span: kept as is.
      spans.push(held);
    } else if (current.from > merged.to) {
      // Wholly above it: the merged span goes in first, once, then this one.
      if (!placed) {
        spans.push({ from: iso(merged.from), to: iso(merged.to) });
        placed = true;
      }
      spans.push(held);
    } else {
      // Overlapping or touching: folded into the merged span, which keeps growing.
      merged = { from: Math.min(current.from, merged.from), to: Math.max(current.to, merged.to) };
    }
  }
  if (!placed) spans.push({ from: iso(merged.from), to: iso(merged.to) });
  return { spans };
}

/**
 * The gaps between `floor` and the head of the log, newest first.
 *
 * The top gap, from the newest span to the head, is always there: it is what a poll fills. Then each gap between two
 * spans, then the one below the oldest span when it stops more than {@link COVERAGE_FLOOR_SLACK_MS} short of the
 * floor. No spans at all is one gap from the floor to the head: a cold boot.
 */
export function gapsIn(coverage: LogCoverage, floor: Date): Gap[] {
  const spans = coverage.spans;
  const last = spans.at(-1);
  if (last === undefined) return [{ lo: floor, hi: null }];
  const gaps: Gap[] = [{ lo: new Date(last.to), hi: null }];
  for (let i = spans.length - 1; i > 0; i--) {
    const above = spans[i];
    const below = spans[i - 1];
    if (above !== undefined && below !== undefined) gaps.push({ lo: new Date(below.to), hi: new Date(above.from) });
  }
  const first = spans[0];
  if (first !== undefined && parse(first.from) - floor.getTime() > COVERAGE_FLOOR_SLACK_MS) {
    gaps.push({ lo: floor, hi: new Date(first.from) });
  }
  return gaps;
}

/**
 * `gap` cut at every rung edge that falls strictly inside it, the pieces newest first.
 *
 * A delta page runs oldest first, so a large gap filled in one walk would land its newest rows last. Cutting it into
 * rungs and filling them top-down lands the newest rung first, and each lower rung stops where the one above began.
 */
export function rungsOf(gap: Gap, now: Date, ladderHours: readonly number[]): Gap[] {
  const cuts = ladderHours
    .map((hours) => now.getTime() - hours * HOUR_MS)
    .filter((cut) => cut > gap.lo.getTime() && (gap.hi === null || cut < gap.hi.getTime()))
    .sort((a, b) => b - a);
  const rungs: Gap[] = [];
  let hi = gap.hi;
  for (const cut of cuts) {
    rungs.push({ lo: new Date(cut), hi });
    hi = new Date(cut);
  }
  rungs.push({ lo: gap.lo, hi });
  return rungs;
}

/** Pages a gap fill lands between flushes of its progress to the store; see {@link createCoverageSync}. */
export const COVERAGE_FLUSH_EVERY_PAGES = 5;

/** The conditions the engine reports; the binding names the log they are about. */
export type CoverageSyncEvent = "delta_page_cap" | "backfill_failed";

/**
 * The collaborators a coverage sync reads and writes through; each log binds its own.
 *
 * @template TName - The names of the logs this sync serves: a stream, or an acknowledgement subject.
 * @template TPage - One page of the log as the route serves it.
 */
export type CoverageSyncDeps<TName extends string, TPage extends { cursor?: string | null }> = {
  /** How far back the log is held, from the server's clock. */
  depth: { hours: number };
  /** Rung edges in hours before now, ascending; see {@link rungsOf}. The last one is the recent window's edge. */
  ladderHours: readonly number[];
  /** Pages one gap fill may take before it stops, keeping its progress for the next sync to resume. */
  maxPages: number;
  store: CoverageStore;
  /** The store key a log's coverage lives under. */
  keyOf: (name: TName) => string;
  /**
   * One delta page of the named log: rows created after `since`, continued by `cursor` when given.
   *
   * `until` is the gap's upper edge, inclusive, when the gap has one. A log whose route takes `until` sends it, so the
   * walk reads only its own gap; a log that ignores it reads on to the head, and the engine still records only the gap.
   */
  fetchPage: (
    ctx: SyncContext,
    name: TName,
    since: Date,
    cursor: string | undefined,
    until: Date | undefined,
  ) => Promise<TPage>;
  applyPage: (page: TPage) => Promise<void> | void;
  /** The newest `createdAt` on a page; undefined for an empty page. */
  newestCreatedAt: (page: TPage) => Date | undefined;
  /** The server's clock as the client estimates it from every response's `Date` header. */
  now: () => Date;
  /** A recoverable condition worth a reliability event, on the named log. */
  report: (event: CoverageSyncEvent, name: TName, attributes: Record<string, unknown>) => void;
};

export type CoverageSync<TName extends string> = {
  /**
   * Brings the log's coverage up to `[now − depth, now − lag]`.
   *
   * Validates the stored spans, fills every gap that reaches above the recent window's edge newest rung first and
   * waits for them, then fills the rest in the background. Single-flight per workspace and log: a call during a run
   * waits for it and runs once more, so rows a ping announced are fetched without racing the run in flight.
   */
  sync: (ctx: SyncContext, name: TName) => Promise<void>;
  /**
   * Resolves once no background fill is running for the log; at once when none is. Never rejects: a fill that failed
   * is reported and left for the next sync, and this still resolves.
   *
   * A boot that must hold the whole window awaits this after `sync`; a poll or a ping does not.
   */
  backfilled: (ctx: SyncContext, name: TName) => Promise<void>;
};

/**
 * Builds the sync over `deps`.
 *
 * Progress is folded into the spans in memory after every page and flushed to the store when a gap fill ends, for
 * any reason, and every {@link COVERAGE_FLUSH_EVERY_PAGES} pages in between, so a reload mid-load resumes within a
 * few pages of where it stood without a store write per page. The in-memory spans are the truth for the session;
 * writes to the store are chained per log and carry the spans as they stand when the write runs, so a poll and a
 * background rung landing together can never persist the older of their two states.
 */
export function createCoverageSync<TName extends string, TPage extends { cursor?: string | null }>(
  deps: CoverageSyncDeps<TName, TPage>,
): CoverageSync<TName> {
  const foreground = createSingleFlight();
  const background = createSingleFlight();
  /** Coverage as last read or written this session, by lock key; the store is read once per log. */
  const held = new Map<string, LogCoverage>();
  /** The tail of each log's chain of store writes. */
  const writes = new Map<string, Promise<void>>();

  const lockKey = (ctx: SyncContext, name: TName) => `${ctx.workspaceId}:${deps.keyOf(name)}`;
  const floorAt = (now: Date) => new Date(now.getTime() - deps.depth.hours * HOUR_MS);
  const recentEdgeAt = (now: Date) => new Date(now.getTime() - (deps.ladderHours.at(-1) ?? 0) * HOUR_MS);

  async function load(ctx: SyncContext, name: TName, now: Date): Promise<LogCoverage> {
    const key = lockKey(ctx, name);
    const stored = held.get(key) ?? (await deps.store.get(deps.keyOf(name), ctx.workspaceId));
    const coverage = normalizeCoverage(stored, floorAt(now), now);
    held.set(key, coverage);
    return coverage;
  }

  /** Folds `span` into the log's spans in memory; nothing reaches the store until {@link flush}. */
  function record(ctx: SyncContext, name: TName, span: CoveredSpan): void {
    const key = lockKey(ctx, name);
    held.set(key, mergeSpan(held.get(key) ?? EMPTY, span));
  }

  /** Writes the log's spans as they stand when the write runs, after every write already queued for the log. */
  function flush(ctx: SyncContext, name: TName): Promise<void> {
    const key = lockKey(ctx, name);
    const queued = (writes.get(key) ?? Promise.resolve()).then(async () => {
      const coverage = held.get(key);
      if (coverage !== undefined) await deps.store.set(deps.keyOf(name), ctx.workspaceId, coverage);
    });
    writes.set(key, queued);
    return queued;
  }

  /**
   * Fills one gap: delta pages from its `lo`, each folded into the spans as it lands, until the head, the gap's `hi`,
   * or the page cap. The spans are flushed as the fill ends, however it ends.
   *
   * Past `hi` is strict: `since` is exclusive on the server, so rows created exactly at `hi` belong to this gap, and
   * the walk must see a later row before it knows it has them all. A page in the middle records up to its newest
   * `createdAt` less a millisecond, because rows of one write share a `createdAt` and a page boundary can split them;
   * resuming a millisecond early re-reads the tie, and repeats are keyed on `id`.
   *
   * @returns Whether the gap was closed; false when the cap stopped it with its progress kept.
   */
  async function fill(ctx: SyncContext, name: TName, gap: Gap, pace?: () => Promise<void>): Promise<boolean> {
    const since = gap.lo;
    let cursor: string | undefined;
    try {
      for (let page = 0; page < deps.maxPages; page++) {
        const served = await deps.fetchPage(ctx, name, since, cursor, gap.hi ?? undefined);
        const arrived = deps.now();
        await deps.applyPage(served);
        const newest = deps.newestCreatedAt(served);
        if (!served.cursor) {
          // The last page: everything up to the gap's top is held, or, for a gap open to the head, everything created
          // up to the clock, less the lag.
          const to = gap.hi === null ? iso(arrived.getTime() - COVERAGE_HEAD_LAG_MS) : gap.hi.toISOString();
          record(ctx, name, { from: since.toISOString(), to });
          return true;
        }
        if (gap.hi !== null && newest !== undefined && newest.getTime() > gap.hi.getTime()) {
          // Past the span above: the gap is closed exactly at its edge.
          record(ctx, name, { from: since.toISOString(), to: gap.hi.toISOString() });
          return true;
        }
        // More to come: progress up to this page's newest row, less the tie.
        if (newest !== undefined) record(ctx, name, { from: since.toISOString(), to: iso(newest.getTime() - 1) });
        if ((page + 1) % COVERAGE_FLUSH_EVERY_PAGES === 0) await flush(ctx, name);
        cursor = served.cursor;
        await pace?.();
      }
      log.warn("[sync] delta hit max page limit; progress kept for the next sync", { name, maxPages: deps.maxPages });
      deps.report("delta_page_cap", name, { maxPages: deps.maxPages });
      return false;
    } finally {
      await flush(ctx, name);
    }
  }

  /** Every rung of every gap, newest first, as of `now`. */
  async function rungs(ctx: SyncContext, name: TName, now: Date): Promise<Gap[]> {
    const coverage = await load(ctx, name, now);
    return gapsIn(coverage, floorAt(now)).flatMap((gap) => rungsOf(gap, now, deps.ladderHours));
  }

  const isRecent = (rung: Gap, edge: Date) => rung.hi === null || rung.hi.getTime() > edge.getTime();

  /**
   * Fills the rungs below the recent window one at a time, yielding to the UI between pages.
   *
   * Recomputes the gaps before each rung, so a poll that landed meanwhile is never fought. A failure, or the cap, leaves
   * the progress in the spans for the next sync to continue from.
   */
  function warmup(ctx: SyncContext, name: TName): Promise<void> {
    return background.join(lockKey(ctx, name), async () => {
      try {
        for (;;) {
          const now = deps.now();
          const edge = recentEdgeAt(now);
          const next = (await rungs(ctx, name, now)).find((rung) => !isRecent(rung, edge));
          if (next === undefined) return;
          if (!(await fill(ctx, name, next, yieldToUi))) return;
        }
      } catch (error) {
        log.warn("[sync] background load failed; the next sync resumes it", { name, error });
        deps.report("backfill_failed", name, {});
      }
    });
  }

  function sync(ctx: SyncContext, name: TName): Promise<void> {
    return foreground.coalesce(lockKey(ctx, name), async () => {
      const now = deps.now();
      const edge = recentEdgeAt(now);
      for (const rung of await rungs(ctx, name, now)) {
        // Rungs run newest first, so the first one below the recent window is where the background takes over.
        if (!isRecent(rung, edge)) break;
        await fill(ctx, name, rung);
      }
      void warmup(ctx, name);
    });
  }

  function backfilled(ctx: SyncContext, name: TName): Promise<void> {
    return background.inFlight(lockKey(ctx, name)) ?? Promise.resolve();
  }

  return { sync, backfilled };
}

function parse(value: unknown): number {
  return typeof value === "string" ? Date.parse(value) : Number.NaN;
}

function iso(ms: number): string {
  return new Date(ms).toISOString();
}

/** Lets the browser paint and handle input between a background load's pages. */
export function yieldToUi(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

/**
 * The timers and the tab visibility a poller runs on.
 */
export type PollerClock = {
  setTimeout: (run: () => void, delayMs: number) => unknown;
  clearTimeout: (handle: unknown) => void;
  /** Whether the tab is showing; a hidden tab skips its ticks and catches up when it is shown again. */
  visible: () => boolean;
  /** Subscribes to the tab being shown or hidden; returns the unsubscribe. */
  onVisibilityChange: (listener: () => void) => () => void;
  nextDelayMs: () => number;
};

/**
 * The browser's clock: `window` timers and `document.visibilityState`.
 *
 * @param nextDelayMs - The delay before each tick.
 */
export function browserPollerClock(nextDelayMs: () => number): PollerClock {
  return {
    setTimeout: (run, delayMs) => window.setTimeout(run, delayMs),
    clearTimeout: (handle) => window.clearTimeout(handle as number),
    visible: () => document.visibilityState !== "hidden",
    onVisibilityChange: (listener) => {
      document.addEventListener("visibilitychange", listener);
      return () => document.removeEventListener("visibilitychange", listener);
    },
    nextDelayMs,
  };
}

/**
 * Runs `walkAll` on a timer for as long as the workspace is open.
 *
 * Each tick waits for the walk, so a slow server stretches the cadence rather than stacking walks. A hidden tab lets
 * its ticks pass and walks once when shown again.
 *
 * @returns Stops the poller; safe to call more than once.
 */
export function startPolling(walkAll: () => Promise<void>, clock: PollerClock): () => void {
  let stopped = false;
  let handle: unknown;

  const schedule = () => {
    if (stopped) return;
    handle = clock.setTimeout(() => void tick(), clock.nextDelayMs());
  };

  const tick = async () => {
    if (stopped) return;
    if (clock.visible()) await walkAll();
    schedule();
  };

  const unsubscribe = clock.onVisibilityChange(() => {
    if (!stopped && clock.visible()) void walkAll();
  });

  schedule();
  return () => {
    stopped = true;
    clock.clearTimeout(handle);
    unsubscribe();
  };
}

/**
 * One walk at a time per key.
 *
 * `join` hands a caller the walk already running, or starts one. `coalesce` is for a ping: landing on a running walk,
 * it waits for it and then runs once more, so rows the ping announced are fetched without racing the walk in flight,
 * and however many pings land meanwhile, one follow-up runs. `inFlight` is the walk running under a key, if any, for a
 * caller that waits on it without starting one.
 */
export function createSingleFlight(): {
  join: (key: string, run: () => Promise<void>) => Promise<void>;
  coalesce: (key: string, run: () => Promise<void>) => Promise<void>;
  inFlight: (key: string) => Promise<void> | undefined;
} {
  const inFlight = new Map<string, Promise<void>>();
  const followUps = new Set<string>();

  const start = (key: string, run: () => Promise<void>): Promise<void> => {
    const started = run().finally(() => inFlight.delete(key));
    inFlight.set(key, started);
    return started;
  };

  const coalesce = (key: string, run: () => Promise<void>): Promise<void> => {
    const running = inFlight.get(key);
    if (!running) return start(key, run);
    followUps.add(key);
    return running.then(() => (followUps.delete(key) ? coalesce(key, run) : undefined));
  };

  return { join: (key, run) => inFlight.get(key) ?? start(key, run), coalesce, inFlight: (key) => inFlight.get(key) };
}
